import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export type OnboardingScreen =
    | 'onboarding'
    | 'register'
    | 'select-type'
    | 'instagram-username'
    | 'forgot-password'
    | 'email-send'
    | 'create-username'

export type AccountType = 'collector' | 'creator'

export type AuthMethod = 'email' | 'google' | 'apple'

export interface RegistrationData {
    fullName?: string
    email?: string
    password?: string
    accountType?: AccountType
    username?: string
    instagramUsername?: string
    avatarUrl?: string
    preference?: string
    authMethod?: AuthMethod
    registrationToken?: string
    pricingPlan?: 'free' | 'pro' | 'max'
}

interface OnboardingState {
    // Form data
    registrationData: RegistrationData
    setRegistrationData: (data: Partial<RegistrationData>) => void
    resetRegistrationData: () => void

    // Progress tracking (useful even with router)
    completedSteps: string[]
    markStepCompleted: (step: string) => void
    isStepCompleted: (step: string) => boolean
    resetCompletedSteps: () => void

    // Hydration state
    _hasHydrated: boolean
    setHasHydrated: (hasHydrated: boolean) => void

    // Utility getters (can be simplified if relying on router state)
    isRegistrationComplete: () => boolean
    isCreatorAccount: () => boolean
    isCollectorAccount: () => boolean
}

const initialState: RegistrationData = {
    fullName: undefined,
    email: undefined,
    password: undefined,
    accountType: undefined,
    username: undefined,
    instagramUsername: undefined,
    avatarUrl: undefined,
    preference: undefined,
    authMethod: undefined,
    registrationToken: undefined,
}

export const useOnboardingStore = create<OnboardingState>()(
    persist(
        (set, get) => ({
            // Form data
            registrationData: initialState,
            setRegistrationData: (data) =>
                set((state) => ({
                    registrationData: { ...state.registrationData, ...data },
                })),
            resetRegistrationData: () =>
                set({ registrationData: initialState, completedSteps: [] }),

            // Progress tracking
            completedSteps: [],
            markStepCompleted: (step) =>
                set((state) => ({
                    completedSteps: state.completedSteps.includes(step)
                        ? state.completedSteps
                        : [...state.completedSteps, step],
                })),
            isStepCompleted: (step) => get().completedSteps.includes(step),
            resetCompletedSteps: () => set({ completedSteps: [] }),

            // Hydration state
            _hasHydrated: false,
            setHasHydrated: (hasHydrated) => {
                set({ _hasHydrated: hasHydrated })
            },

            // Derived state getters
            isRegistrationComplete: () => {
                const { registrationData } = get()
                const hasCredentials =
                    !!registrationData.password ||
                    registrationData.authMethod === 'google' ||
                    registrationData.authMethod === 'apple'
                return !!(
                    registrationData.fullName &&
                    registrationData.email &&
                    registrationData.accountType &&
                    hasCredentials &&
                    registrationData.username
                )
            },
            isCreatorAccount: () =>
                get().registrationData.accountType === 'creator',
            isCollectorAccount: () =>
                get().registrationData.accountType === 'collector',
        }),
        {
            name: 'onboarding-storage',
            storage: createJSONStorage(() => sessionStorage),
            onRehydrateStorage: () => (state) => {
                // Mark hydration as complete after rehydration
                state?.setHasHydrated(true)
            },
        }
    )
)

/**
 * Wait for the onboarding store to hydrate from sessionStorage
 * This is needed for route guards that run before React components mount
 * @param maxWaitMs Maximum time to wait in milliseconds (default: 1000ms)
 * @returns Promise that resolves when hydration is complete
 */
export async function waitForOnboardingHydration(
    maxWaitMs: number = 1000
): Promise<void> {
    // If already hydrated, return immediately
    if (useOnboardingStore.getState()._hasHydrated) {
        return
    }

    return new Promise((resolve) => {
        let unsubscribe: (() => void) | undefined

        const timeout = setTimeout(() => {
            unsubscribe?.()
            console.warn('Onboarding hydration timed out')
            resolve()
        }, maxWaitMs)

        unsubscribe = useOnboardingStore.subscribe((state) => {
            if (state._hasHydrated) {
                clearTimeout(timeout)
                unsubscribe?.()
                resolve()
            }
        })
    })
}
